import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RenewalBadge, RiskBadge, Stat } from "@/components/crm/parts";
import { SalesShell } from "@/components/crm/SalesShell";
import { SalesActions } from "@/components/crm/SalesActions";
import { pipelineSheets } from "@/lib/exports/crm";
import { pipelineVisual } from "@/lib/crm/visual-sources";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";
import { commercialPrompts } from "@/lib/crm/suggestions";
import { useAccounts, useContracts, useOpportunities, useStages } from "@/lib/crm/useCrm";
import { daysUntil, gbp, isLiveContract, isOpen, money, weightedValue, RENEWAL_BANDS } from "@/lib/crm/types";

export const Route = createFileRoute("/_authenticated/sales/")({
  head: () => ({
    meta: [
      { title: "Sales overview — MyPriorities" },
      {
        name: "description",
        content:
          "Weighted pipeline, renewals due, at-risk revenue and the commercial actions that need attention this week.",
      },
      { property: "og:title", content: "Sales overview — MyPriorities" },
      {
        property: "og:description",
        content: "See pipeline, renewals and revenue at risk in one commercial view.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SalesOverviewPage,
});

function SalesOverviewPage() {
  return (
    <SalesShell
      title="Sales overview"
      subtitle="Pipeline, renewals and revenue at risk — with the next actions that protect them."
    >
      {({ orgId }) => <Overview orgId={orgId} />}
    </SalesShell>
  );
}

function Overview({ orgId }: { orgId: string }) {
  const stages = useStages(orgId);
  const opportunities = useOpportunities(orgId);
  const contracts = useContracts(orgId);
  const accounts = useAccounts(orgId);

  const { scope } = useSalesOwner();
  const { ownerLabel } = useOrgMembers(orgId);
  const stageRows = stages.data ?? [];
  const oppRows = scope(opportunities.data ?? []);
  const contractRows = scope(contracts.data ?? []);

  const open = oppRows.filter((o) => isOpen(o, stageRows));
  const gross = open.reduce((s, o) => s + money(o.expected_value), 0);
  const weighted = open.reduce((s, o) => s + weightedValue(o, stageRows), 0);
  const wonStageIds = new Set(stageRows.filter((s) => s.is_won).map((s) => s.id));
  const won = oppRows.filter((o) => o.stage_id && wonStageIds.has(o.stage_id));
  const wonValue = won.reduce((s, o) => s + money(o.expected_value), 0);
  const thisYear = String(new Date().getFullYear());
  const wonThisYear = won.filter((o) => (o.expected_close_date ?? "").startsWith(thisYear));
  const wonThisYearValue = wonThisYear.reduce((s, o) => s + money(o.expected_value), 0);

  const liveContractRows = contractRows.filter(isLiveContract);
  const contractedRevenue = liveContractRows.reduce((s, c) => s + money(c.annual_value), 0);
  const atRisk = liveContractRows
    .filter((c) => c.risk_status === "at_risk" || c.risk_status === "expected_loss")
    .reduce((s, c) => s + money(c.annual_value), 0);

  const bands = useMemo(
    () =>
      RENEWAL_BANDS.map((band) => {
        const rows = contractRows.filter((c) => {
          const days = daysUntil(c.end_date);
          return days !== null && days >= 0 && days / 30.44 <= band;
        });
        return {
          band,
          count: rows.length,
          value: rows.reduce((s, c) => s + money(c.annual_value), 0),
        };
      }),
    [contractRows],
  );

  const prompts = useMemo(
    () =>
      commercialPrompts({
        opportunities: oppRows,
        contracts: contractRows,
        stages: stageRows,
        taskCountByRecord: {},
      }),
    [oppRows, contractRows, stageRows],
  );

  const renewalsSoon = contractRows
    .filter((c) => {
      const days = daysUntil(c.end_date);
      return days !== null && days >= 0 && days <= 180;
    })
    .sort((a, b) => (a.end_date ?? "").localeCompare(b.end_date ?? ""))
    .slice(0, 8);

  const accountName = (id: string | null) => accounts.data?.find((a) => a.id === id)?.name ?? "—";

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap justify-end">
        <SalesActions
          exports={[
            {
              label: "Pipeline",
              baseName: "sales-overview",
              sheets: () =>
                pipelineSheets({
                  opportunities: oppRows,
                  accounts: accounts.data ?? [],
                  stages: stageRows,
                  ownerLabel,
                }),
            },
          ]}
          visual={() => pipelineVisual(oppRows, stageRows)}
          sourceNote="From your sales overview"
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Open pipeline" value={gbp(gross)} hint={`${open.length} opportunities`} />
        <Stat label="Weighted pipeline" value={gbp(weighted)} hint="Stage-weighted" />
        <Stat
          label={`Closed won ${thisYear}`}
          value={gbp(wonThisYearValue)}
          hint={`${wonThisYear.length} deals closing this year`}
        />
        <Stat
          label="Closed won (all time)"
          value={gbp(wonValue)}
          hint={`${won.length} deals, full won history`}
        />
        <Stat
          label="Contracted revenue"
          value={gbp(contractedRevenue)}
          hint={`Annualised across ${contractRows.length} contracts`}
        />
        <Stat label="Revenue at risk" value={gbp(atRisk)} hint="Flagged at risk or expected loss" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Renewals ahead</CardTitle>
          <CardDescription>Annual value falling due inside each warning band.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-5">
          {bands.map((b) => (
            <div key={b.band} className="rounded-lg border border-border p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Within {b.band} {b.band === 1 ? "month" : "months"}
              </p>
              <p className="mt-1 font-display text-lg font-semibold">{gbp(b.value)}</p>
              <p className="text-[11px] text-muted-foreground">{b.count} contracts</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What needs attention</CardTitle>
          <CardDescription>
            Commercial prompts, not just a list of what's already sold. Nothing is actioned until
            you choose it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {prompts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing pressing — every open opportunity has a next action and no renewals are
              uncovered.
            </p>
          ) : (
            <ul className="grid gap-2">
              {prompts.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                >
                  <div>
                    <p className="text-sm font-medium">{p.title}</p>
                    <p className="text-[11px] text-muted-foreground">{p.detail}</p>
                  </div>
                  <Button size="sm" variant="outline" asChild>
                    <Link to={p.href}>Open</Link>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Next renewals</CardTitle>
          <CardDescription>The contract book, closest renewal first.</CardDescription>
        </CardHeader>
        <CardContent>
          {renewalsSoon.length === 0 ? (
            <p className="text-sm text-muted-foreground">No renewals inside six months.</p>
          ) : (
            <ul className="grid gap-2">
              {renewalsSoon.map((c) => (
                <li
                  key={c.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                >
                  <div>
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground">{accountName(c.account_id)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{gbp(c.annual_value)}</Badge>
                    <RenewalBadge endDate={c.end_date} />
                    <RiskBadge status={c.risk_status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
